import React, { useContext } from 'react';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { OverageTopText } from './DisclosureUnits/OverageTopText';
import { ShortageTopText } from './DisclosureUnits/ShortageTopText';
import { useNetCalculation } from '../../../hooks/netGrossCalculations';
import { LAST_SECTION_INDEX, MONTHS_IN_YEAR } from '../../../config/constants';
import {
    useShortageOrSurplus,
    useSurplusAvailableToSolveMissingAow,
    useTotalShortage,
} from '../hooks/useSurplusCorrected';
import { useIsJointCalculation, useLegalRetirementAge } from '../hooks/usePensionDate';
import { IconElements } from '../../../config/icons';

export const RetirementWarning = () => {
    const shortageOrSurplus = useShortageOrSurplus();
    const surplusForResolvingAOW = useSurplusAvailableToSolveMissingAow();
    const calculatedTogether = useIsJointCalculation();
    const missingAow = useMissedAOW();
    const totalShortage = useTotalShortage();
    const someoneHasShortage =
        shortageOrSurplus.partnerShortageOrSurplusPerYear < 0 || shortageOrSurplus.selfShortageOrSurplusPerYear < 0;
    const bothHaveSurplus = !someoneHasShortage;
    const notCorrectedAow =
        surplusForResolvingAOW && surplusForResolvingAOW > missingAow ? 0 : missingAow - (surplusForResolvingAOW ?? 0);
    const aowIsMissed = missingAow > 0;

    // Calculation with partner
    if (calculatedTogether) {
        if (bothHaveSurplus) {
            return <OverageTopText goal={shortageOrSurplus.goal} notFullfilledMissingAOW={notCorrectedAow} />;
        }

        if (someoneHasShortage) {
            return (
                <ShortageTopText amountNeedToReachPensionGoal={totalShortage + missingAow} aowMissing={aowIsMissed} />
            );
        }
    }

    // Calculation as single
    if (!calculatedTogether) {
        const selfHasSurplus = shortageOrSurplus.selfShortageOrSurplusPerYear > 0;
        // Self has a surplus
        if (selfHasSurplus) {
            return <OverageTopText goal={shortageOrSurplus.goal} notFullfilledMissingAOW={notCorrectedAow} />;
        }

        // Self has shortage
        return <ShortageTopText amountNeedToReachPensionGoal={totalShortage + missingAow} aowMissing={aowIsMissed} />;
    }

    return <></>;
};

export const ShortageWarning = ({ text }: { text: string }) => {
    return (
        <div className="text-bnd-yellow flex flex-row items-center mb-4">
            <span className="text-6xl mr-4">!</span>
            <span>
                <span>{text}</span>
            </span>
        </div>
    );
};

export const SurplusNoWarning = ({ text }: { text: string }) => {
    return (
        <div className="text-bnd-green flex flex-row items-center mb-4 mt-2">
            <span className="text-6xl mr-4">
                <IconElements.Check className="w-6 h-6" />
            </span>
            <span>{text}</span>
        </div>
    );
};

export const useMissedAOW = () => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const legalRetirementAge = useLegalRetirementAge();
    const calculatedTogether = useIsJointCalculation();

    const netValuesAtRetirementAgeOfSelf = useNetCalculation(LAST_SECTION_INDEX, false);
    const netValuesAtRetirementAgeOfPartner = useNetCalculation(LAST_SECTION_INDEX, true);

    const selfPensionAge = personalInfo.self.pensionAge;
    const partnerPensionAge = personalInfo.partner.pensionAge;

    const missedAowTotalSelf =
        (legalRetirementAge - selfPensionAge) * (netValuesAtRetirementAgeOfSelf.netAow.me.monthly * MONTHS_IN_YEAR);
    const missedAowTotalPartner =
        (legalRetirementAge - partnerPensionAge) *
        (netValuesAtRetirementAgeOfPartner.netAow.partner.monthly * MONTHS_IN_YEAR);

    const selfAowMissedBecauseYoungerThanPengionAge = selfPensionAge < legalRetirementAge ? missedAowTotalSelf : 0;

    if (!calculatedTogether) return selfAowMissedBecauseYoungerThanPengionAge;

    const partnerAowMissedBecauseYoungerThanPengionAge =
        partnerPensionAge < legalRetirementAge ? missedAowTotalPartner : 0;
    return selfAowMissedBecauseYoungerThanPengionAge + partnerAowMissedBecauseYoungerThanPengionAge;
};
